import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useBrandConfig } from "src/brand/config";
import { CustomerFolderTypeCode } from "src/core/constants/folder";
import { CreateClientEIFormValues } from "src/core/forms/create-client/createClientEI.schema";
import { CreateClientRaisonSocialeFormValues } from "src/core/forms/create-client/createClientRaisonSociale.schema";
import { CreateClientSirenFormValues } from "src/core/forms/create-client/createClientSiren.schema";
import {
  useAutoCreateCustomerGraphMutation,
  useCallEReputationMutation,
  useCreateClientByCorporationIdMutation,
  useCreateClientByRaisonSocialeMutation,
  useCreateClientPphByPersonIdMutation,
  useCreateClientPphMutation,
  useFollowFolderMutation,
} from "src/core/gql/graphql";
import { useRequiredTenantPaths } from "src/core/hooks/routing/useTenantPaths";
import { ICustomDuplicateFolder } from "src/core/interfaces/Folders";
import {
  buildCreateClientByRaisonSocialeInputValues,
  buildCreateClientPPHInputValues,
} from "src/core/utils/folder";
import { buildFolderFilesCountRefetch } from "src/core/utils/refetchers";

export const useCreateClient = () => {
  const { t } = useTranslation();

  const paths = useRequiredTenantPaths();
  const navigate = useNavigate();

  const { createFolderFile } = useBrandConfig();
  const [autoCreateCustomerGraph] = useAutoCreateCustomerGraphMutation();
  const [createClientByRaisonSociale] = useCreateClientByRaisonSocialeMutation();
  const [createClientByCorporationId] = useCreateClientByCorporationIdMutation();
  const [createClientPphByPersonId] = useCreateClientPphByPersonIdMutation();
  const [createClientPPH] = useCreateClientPphMutation();
  const [callEReputation] = useCallEReputationMutation();
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [followFolder] = useFollowFolderMutation();

  const navigateTo = (id?: string | null) => {
    if (id) navigate(paths.folder(id));
  };

  /**
   * @description Vérifie si la corporation est déjà associés à un dossier client.
   * @returns {boolean} true si la corporation est déjà associés à un dossier client, false sinon.
   */
  const shouldSkipAutoCreation = (duplicates: ICustomDuplicateFolder[]): boolean =>
    duplicates.length > 0 && duplicates[0].isCustomer === true;

  const tryAutoCreateCustomerGraph = async (siren: string) => {
    try {
      // on essaie la création automatique par siren
      // le back renvoie une erreur si le siren n'est pas trouvé donc on doit rajouter un try catch
      const { data: corporation } = await autoCreateCustomerGraph({
        variables: {
          siren: siren,
          includeIntermediateLegalRepresentatives: false,
        },
      });

      return corporation?.autoCreateCustomerGraph?.customer?.id;
    } catch (error) {
      console.error(
        "error lors de la création automatique par siren (autoCreateCustomerGraph)",
        error
      );
    }

    return undefined;
  };

  const isSirenForm = (
    v: CreateClientSirenFormValues | CreateClientRaisonSocialeFormValues
  ): v is CreateClientSirenFormValues => "siren" in v;

  const createByRaisonSociale = async (
    data: CreateClientSirenFormValues | CreateClientRaisonSocialeFormValues
  ) => {
    const commonVars = getCommonVars(data);

    const name = isSirenForm(data)
      ? t("form.createClient.emptyCorporationName")
      : data.raisonSociale;

    const siren = isSirenForm(data) ? data.siren : null;

    const { data: folderData } = await createClientByRaisonSociale({
      variables: {
        ...commonVars,
        inputValues: buildCreateClientByRaisonSocialeInputValues(name, siren),
      },
      // Harmonisation : on garde le même comportement que la version SIREN
      refetchQueries: buildFolderFilesCountRefetch(),
      awaitRefetchQueries: true,
    });
    const customerFolderId = folderData?.createCustomerFolder?.id;
    if (customerFolderId && customerFolderId !== null) {
      if (data.isFollowing) {
        await followFolder({ variables: { customerFolderId } });
      }
      if (data.folowers && data.folowers.length > 0) {
        Promise.all(
          data.folowers.map(async (userId) => {
            await followFolder({
              variables: { customerFolderId, userId },
            });
          })
        );
      }
    }

    return mapCreateClientResponseToFolderId(folderData);
  };

  const createByCorporationId = async (
    data: CreateClientSirenFormValues,
    corpId: string,
    optionCustomerFolderTypeCode: string = CustomerFolderTypeCode.ENTREPRISE
  ) => {
    const commonVars = getCommonVars(data);

    const { data: folderData } = await createClientByCorporationId({
      variables: {
        ...commonVars,
        corporationId: corpId,
        optionCustomerFolderTypeCode: optionCustomerFolderTypeCode,
      },
    });
    const customerFolderId = folderData?.createCustomerFolder?.id;
    if (customerFolderId && customerFolderId !== null) {
      if (data.isFollowing) {
        await followFolder({ variables: { customerFolderId } });
      }
      if (data.folowers && data.folowers.length > 0) {
        Promise.all(
          data.folowers.map(async (userId) => {
            await followFolder({
              variables: { customerFolderId, userId },
            });
          })
        );
      }
    }
    const id = mapCreateClientResponseToFolderId(folderData);

    if (createFolderFile && folderData?.createFolderFile?.id) {
      const folderFileId = folderData.createFolderFile.id;

      try {
        await callEReputation({
          variables: { folderFileId },
          errorPolicy: "all",
          refetchQueries: buildFolderFilesCountRefetch(),
          awaitRefetchQueries: true,
          context: {
            suppressRedirect: true,
          },
        });
      } catch (error) {
        console.error("[useCreateClient::createByCorporationId] callEReputation failed", {
          error,
          folderFileId,
        });
      }
    }

    return id;
  };

  const createPphByPersonId = async (data: CreateClientSirenFormValues, personId: string) => {
    const commonVars = getCommonVars(data);

    const { data: folderData } = await createClientPphByPersonId({
      variables: {
        ...commonVars,
        personId: personId,
      },
    });
    const customerFolderId = folderData?.createCustomerFolder?.id;
    if (customerFolderId && customerFolderId !== null) {
      if (data.isFollowing) {
        await followFolder({ variables: { customerFolderId } });
      }
      if (data.folowers && data.folowers.length > 0) {
        Promise.all(
          data.folowers.map(async (userId) => {
            await followFolder({
              variables: { customerFolderId, userId },
            });
          })
        );
      }
    }
    const id = mapCreateClientResponseToFolderId(folderData);

    if (createFolderFile && folderData?.createFolderFile?.id) {
      const folderFileId = folderData.createFolderFile.id;

      try {
        await callEReputation({
          variables: { folderFileId },
          errorPolicy: "all",
          refetchQueries: buildFolderFilesCountRefetch(),
          awaitRefetchQueries: true,
          context: {
            suppressRedirect: true,
          },
        });
      } catch (error) {
        console.error("[useCreateClient::createByCorporationId] callEReputation failed", {
          error,
          folderFileId,
        });
      }
    }

    return id;
  };

  const createPPH = async (data: CreateClientEIFormValues | CreateClientSirenFormValues) => {
    const commonVars = getCommonVars(data);

    const { data: folderData } = await createClientPPH({
      variables: {
        ...commonVars,
        inputValues: buildCreateClientPPHInputValues(data, t),
      },
      // Harmonisation : on garde le même comportement que la version SIREN
      refetchQueries: buildFolderFilesCountRefetch(),
      awaitRefetchQueries: true,
    });
    const customerFolderId = folderData?.createCustomerFolder?.id;
    if (customerFolderId && customerFolderId !== null) {
      if (data.isFollowing) {
        await followFolder({ variables: { customerFolderId } });
      }
      if (data.folowers && data.folowers.length > 0) {
        Promise.all(
          data.folowers.map(async (userId) => {
            await followFolder({
              variables: { customerFolderId, userId },
            });
          })
        );
      }
    }

    return mapCreateClientResponseToFolderId(folderData);
  };

  const mapCreateClientResponseToFolderId = (
    data:
      | Awaited<ReturnType<typeof createClientByCorporationId>>["data"]
      | Awaited<ReturnType<typeof createClientByRaisonSociale>>["data"]
  ) => {
    if (createFolderFile) {
      return data?.createFolderFile?.id;
    }

    return data?.createCustomerFolder?.id;
  };

  type CommonFormFields = {
    prenom?: string | null;
    nom?: string | null;
    email?: string | null;
    telephone?: string | null;
    expertComptable?: string | null;
  };

  /**
   * La fonction getCommonVars extrait et transforme les champs communs d'un formulaire
   * de création de client. Elle gère :
   * - Les informations de contact (prénom, nom, email, téléphone)
   * - L'expert-comptable assigné
   * - Le flag showContactInfo qui détermine si les contacts sont inclus
   * - Le flag createFolderFile de la configuration de marque
   */
  const getCommonVars = <T extends CommonFormFields>(data: T) => {
    const { prenom, nom, email, telephone, expertComptable } = data;
    const isContact = showContactInfo && Boolean(prenom || nom || email || telephone);
    const isOwner = expertComptable !== "none" && expertComptable !== "" && !!expertComptable;

    return {
      isContact,
      isOwner,
      ownerId: isOwner ? expertComptable : "",
      isFolderFile: createFolderFile,
      // Les champs de contact sont optionnels, on ne les ajoute que si ils sont remplis ET si showContactInfo est activé
      ...(showContactInfo && prenom != null && prenom !== "" && { firstName: prenom }),
      ...(showContactInfo && nom != null && nom !== "" && { lastName: nom }),
      ...(showContactInfo && email != null && email !== "" && { email }),
      ...(showContactInfo && telephone != null && telephone !== "" && { phoneNumber: telephone }),
    };
  };

  return {
    tryAutoCreateCustomerGraph,
    createByRaisonSociale,
    createByCorporationId,
    createPPH,
    createPphByPersonId,
    showContactInfo,
    setShowContactInfo,
    shouldSkipAutoCreation,
    navigateTo,
  };
};
